import { AreaTypes, TimeOfDay, GeometryTypes } from "@/helpers/enums"
import AreaUtils from "@/helpers/areaUtils"
import ApiDataModel from "@/models/apidatamodel"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import TableData from "@/models/tabledata"
import { store } from '@/store/store'
import i18n from '@/i18n'
import BittimittariUtils from "@/helpers/bittimittariUtils"
import LegendLimitsUtils from "@/helpers/legendLimitsUtils"

const t = i18n.global.t

export default class BittimittariService {
    BittimittariUtils = new BittimittariUtils()
    AreaUtils = new AreaUtils()
    apiCallBasePart: string = "v1/Viestinta/Bittimittari/"
    selectedRadioButton: string = ""
    LegendLimitsUtils = new LegendLimitsUtils()

    vuorokaudenaikaanPohjautuvatTiedotLegendLimits = this.LegendLimitsUtils.getLegendLimits("BittimittariVuorokaudenaikaanPohjautuvatTiedot")
    operaattoriDownloadLegendLimits = this.LegendLimitsUtils.getLegendLimits("BittimittariOperaattoriDownload")
    operaattoriUploadLegendLimits = this.LegendLimitsUtils.getLegendLimits("BittimittariOperaattoriUpload")
    vuorokaudenaikaRuudutDownloadLegendLimits = this.LegendLimitsUtils.getLegendLimits("BittimittariVuorokaudenaikaRuudutDownload")

    get currentLanguage() {
        return store.getters.activeLang
    }

    async handleBittimittariVuorokausi(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            let isRightSlotDataUpdated = false

            data.forEach((value) => {
                // Tässä käydään läpi DataPublishServicen palauttamat kunta/maakuntakohtaiset/koko maa datat ja asetetaan ne modeliin.
                let id: string = "0"
                const color = this.getVuorokausiLegendColor(value.mediaanidownloadnopeus)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Country) {
                    id = "1"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                let textContent = ""

                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.Bittimittari.EntireFinland") ?? ""

                    const tableValues: Array<Array<string>> = []
                    tableValues.push([
                        this.createVuorokaudenAikaTableString(value.vuorokaudenaikaid),
                        this.createMediaaniDownloadTableString(value),
                        value.mittaustenlkm?.toString()
                    ])

                    rightSlotData.tableData = new TableData(this.bittimittariVuorokausiTableColumnHeaders, tableValues, null)
                    textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Country")
                }
                else {
                    rightSlotData.municipality = this.BittimittariUtils.getName(value)
                    if (areaType === AreaTypes.Municipality) {
                        textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Municipality")
                    }
                    else if (areaType === AreaTypes.Province) {
                        textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Province")
                    }
                }

                rightSlotData.dataUpdatedDate = textContent + this.getVuorokaudenaikaUpdatedtext()

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id)

                if (areaType === AreaTypes.Country) {
                    // Koko maan tapauksessa righslotin sisältö asetetaan näkyviin jo tässä vaiheessa,
                    // koska aluetta ei valita erikseen kartalta
                    store.dispatch("setRightSlotData", rightSlotData)
                    isRightSlotDataUpdated = true
                }
            })

            if (!isRightSlotDataUpdated) {
                // Näytetään rightslotin alareunan infotekstit vaikka aluetta ei olisi valittu
                const rightSlotData = new DynamicRightSlotData("0")
                let textContent = ""
                if (areaType === AreaTypes.Municipality) {
                    textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Municipality")
                }
                else if (areaType === AreaTypes.Province) {
                    textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Province")
                }
                rightSlotData.dataUpdatedDate = textContent + this.getVuorokaudenaikaUpdatedtext()

                store.dispatch("setRightSlotData", rightSlotData)
            }
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleBittimittariOperaattorikohtaisetTiedot(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)
            const dataDirection = store.getters.dataTransferDirection

            data.forEach((value) => {
                // Tässä käydään läpi DataPublishServicen palauttamat kunta/maakuntakohtaiset datat ja asetetaan ne modeliin.
                let id: string = "0"

                // Asetetaan kartalle data sen mukaan, onko download vai upload valittuna
                let color = ""
                if (dataDirection === "Download") {
                    color = this.getOperaattoriDownloadLegendColor(value.mediaanidownloadnopeus)
                }
                else if (dataDirection === "Upload") {
                    color = this.getOperaattoriUploadLegendColor(value.mediaaniuploadnopeus)
                }

                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)

                rightSlotData.municipality = this.BittimittariUtils.getName(value)

                let textContent = ""
                if (areaType === AreaTypes.Municipality) {
                    textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Municipality")
                }
                else if (areaType === AreaTypes.Province) {
                    textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Province")
                }

                rightSlotData.dataUpdatedDate = textContent + this.getOperaattoriUpdatedtext()

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id)
            })

            // Näytetään rigthslotin alareunan infotekstit vaikka aluetta ei olisi valittu
            const rightSlotData = new DynamicRightSlotData("0")
            let textContent = ""
            if (areaType === AreaTypes.Municipality) {
                textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Municipality")
            }
            else if (areaType === AreaTypes.Province) {
                textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Province")
            }

            rightSlotData.dataUpdatedDate = textContent + this.getOperaattoriUpdatedtext()
            store.dispatch("setRightSlotData", rightSlotData)

            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleBittimittariVuorokaudenaikaanPohjautuvatRuudut(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            data.forEach((item, index) => {
                const options = {
                    fillColor: this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(item.mediaanidownloadnopeus),
                    fillOpacity: 0.8,
                    color: "#666",
                    opacity: 0.8,
                    weight: 1,
                    fill: true,
                    stroke: true,
                    id: index
                }

                item.koordinaatit.options = options
                item.koordinaatit.allowedGeometryTypes = [GeometryTypes.Polygon]

                apiData.push(new ApiDataModel(index.toString(), true, item.koordinaatit, item, null, null))
            })

            return apiData
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    handleBittimittariVuorokausiRightslotData(id: string, data, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []
        let textContent = ""

        // Näytettävä tekstisisältö
        if (data[0].kuntanumero !== null) {
            textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Municipality")
        }
        else if (data[0].maakuntanumero !== null) {
            textContent = t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.RightSlot.TextContent.Province")
        }

        if (data[0].notenoughmeasurements) {
            rightSlotData.description = t("App.Bittimittari.RightSlot.NotEnoughMeasurements")
        }
        else {
            // Taulukon sisältö
            data.forEach((tilasto) => {
                tableValues.push([
                    this.createVuorokaudenAikaTableString(tilasto.vuorokaudenaikaid),
                    this.createMediaaniDownloadTableString(tilasto),
                    tilasto.mittaustenlkm?.toString()
                ])
            })
            rightSlotData.tableData = new TableData(this.bittimittariVuorokausiTableColumnHeaders, tableValues, null)
        }

        rightSlotData.municipality = municipality
        rightSlotData.dataUpdatedDate = textContent + this.getVuorokaudenaikaUpdatedtext()

        return rightSlotData
    }

    handleOperaattorikohtaisetTiedotRightslotData(id: string, data, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []
        let textContent = ""
        const operatorData = data[0]
        const dataDirection = store.getters.dataTransferDirection

        // Näytettävä tekstisisältö
        if (operatorData.kuntanumero !== null) {
            textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Municipality")
        }
        else if (operatorData.maakuntanumero !== null) {
            textContent = t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.TextContent." + dataDirection + ".Province")
        }

        if (operatorData.notenoughmeasurements) {
            rightSlotData.description = t("App.Bittimittari.RightSlot.NotEnoughMeasurements")
        }
        else {
            tableValues.push([
                this.currentLanguage === "sv" ? operatorData.namn : operatorData.nimi,
                this.createMediaaniDownloadTableString(operatorData),
                this.createMediaaniUploadTableString(operatorData),
                operatorData.mittaustenlkm?.toString()])

            rightSlotData.tableData = new TableData(this.bittimittariOperaattoriTableColumnHeaders, tableValues, null)
        }

        rightSlotData.municipality = municipality
        rightSlotData.dataUpdatedDate = textContent + this.getOperaattoriUpdatedtext()

        return rightSlotData
    }

    getBittimittariVuorokausiLegend(data): string {
        const limits = this.vuorokaudenaikaanPohjautuvatTiedotLegendLimits
        const areaType = this.AreaUtils.getAreaTypeFromData(data)
        if (areaType !== AreaTypes.Country) {
            let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(-1) + "\"></i><div>" + t("App.Bittimittari.Legend.NotEnoughMeasurements") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[0] - 1) + "\"></i><div>" + t("App.Bittimittari.Legend.LessThan") + " " + limits[0] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[1]) + "\"></i><div>" + limits[0] + " - " + limits[1] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[2]) + "\"></i><div>" + limits[1] + " - " + limits[2] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[3]) + "\"></i><div>" + limits[2] + " - " + limits[3] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[4]) + "\"></i><div>" + limits[3] + " - " + limits[4] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[5]) + "\"></i><div>" + limits[4] + " - " + limits[5] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokausiLegendColor(limits[5] + 1) + "\"></i><div>" + limits[5] + "+ " + "Mbit/s" + "</div></div>"

            return legend
        }

        return ""
    }

    getBittimittariOperaattoriDownloadLegend(data): string {
        const limits = this.operaattoriDownloadLegendLimits
        const areaType = this.AreaUtils.getAreaTypeFromData(data)
        if (areaType !== AreaTypes.Country) {
            let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(-1) + "\"></i><div>" + t("App.Bittimittari.Legend.NotEnoughMeasurements") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[0] - 1) + "\"></i><div>" + t("App.Bittimittari.Legend.LessThan") + " " + limits[0] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[1]) + "\"></i><div>" + limits[0] + " - " + limits[1] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[2]) + "\"></i><div>" + limits[1] + " - " + limits[2] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[3]) + "\"></i><div>" + limits[2] + " - " + limits[3] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[4]) + "\"></i><div>" + limits[3] + " - " + limits[4] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[5]) + "\"></i><div>" + limits[4] + " - " + limits[5] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriDownloadLegendColor(limits[5] + 1) + "\"></i><div>" + limits[5] + "+ " + "Mbit/s" + "</div></div>"

            return legend
        }

        return ""
    }

    getBittimittariOperaattoriUploadLegend(data): string {
        const limits = this.operaattoriUploadLegendLimits
        const areaType = this.AreaUtils.getAreaTypeFromData(data)
        if (areaType !== AreaTypes.Country) {
            let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(-1) + "\"></i><div>" + t("App.Bittimittari.Legend.NotEnoughMeasurements") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[0] - 1) + "\"></i><div>" + t("App.Bittimittari.Legend.LessThan") + " " + limits[0] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[1]) + "\"></i><div>" + limits[0] + " - " + limits[1] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[2]) + "\"></i><div>" + limits[1] + " - " + limits[2] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[3]) + "\"></i><div>" + limits[2] + " - " + limits[3] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[4]) + "\"></i><div>" + limits[3] + " - " + limits[4] + " " + "Mbit/s" + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getOperaattoriUploadLegendColor(limits[4] + 1) + "\"></i><div>" + limits[4] + "+ " + "Mbit/s" + "</div></div>"

            return legend
        }

        return ""
    }

    getBittimittariVuorokaudenaikaanPohjautuvatRuudutLegend(): string {
        const limits = this.vuorokaudenaikaRuudutDownloadLegendLimits
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[0] - 1) + "\"></i><div>" + t("App.Bittimittari.Legend.LessThan") + " " + limits[0] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[1]) + "\"></i><div>" + limits[0] + " - " + limits[1] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[2]) + "\"></i><div>" + limits[1] + " - " + limits[2] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[3]) + "\"></i><div>" + limits[2] + " - " + limits[3] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[4]) + "\"></i><div>" + limits[3] + " - " + limits[4] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[5]) + "\"></i><div>" + limits[4] + " - " + limits[5] + " " + "Mbit/s" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVuorokaudenaikaanPohjautuvatRuudutLegendColor(limits[5] + 1) + "\"></i><div>" + limits[5] + "+ " + "Mbit/s" + "</div></div>"

        return legend
    }

    getVuorokausiLegendColor(value: number): string {
        const limits = this.vuorokaudenaikaanPohjautuvatTiedotLegendLimits
        return this.BittimittariUtils.getLegendColor(value, limits)
    }

    getOperaattoriDownloadLegendColor(value: number): string {
        const limits = this.operaattoriDownloadLegendLimits
        return this.BittimittariUtils.getLegendColor(value, limits)
    }

    getOperaattoriUploadLegendColor(value: number): string {
        const limits = this.operaattoriUploadLegendLimits
        return this.BittimittariUtils.getOperaattoriUploadLegendColor(value, limits)
    }

    getVuorokaudenaikaanPohjautuvatRuudutLegendColor(value: number): string {
        const limits = this.vuorokaudenaikaRuudutDownloadLegendLimits
        return this.BittimittariUtils.getLegendColor(value, limits)
    }

    updateModels(models: ApiDataModel[], rightSlotData: DynamicRightSlotData, color: string, id: string) {
        const item = models.find(e => e.id === id)
        if (item !== undefined) {
            item.rightSlotData = rightSlotData
            item.featureData.koordinaatit.options.fillColor = color
            item.featureData.koordinaatit.options.fillOpacity = color === "" ? 0 : 0.8 // jos ei ole väriä niin täysi läpinäkyvyys
            if (item.geometryData !== null) {
                // Vain ne alueet voi valita kartalta, joilla on dataa
                item.geometryData.handleOnClick = true
            }
        }
    }

    getVuorokaudenaikaUpdatedtext(): string {
        return t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.Includes") + "<br><br>" + t("App.BittimittariVuorokaudenaikaanPohjautuvatTiedot.DataUpdated")
    }

    getOperaattoriUpdatedtext(): string {
        return t("App.BittimittariOperaattorikohtaisetTiedot.RightSlot.DataDirectionInfo") + t("App.BittimittariOperaattorikohtaisetTiedot.Includes") + "<br><br>" + t("App.BittimittariOperaattorikohtaisetTiedot.DataUpdated")
    }

    createVuorokaudenAikaTableString(vuorokaudenaikaid: number): string {
        const timeOfDayString = TimeOfDay[vuorokaudenaikaid]
        const tableCellString = t("App.Bittimittari." + timeOfDayString)
        return tableCellString
    }

    createMediaaniDownloadTableString(value: any): string {
        if (value.mediaanidownloadnopeus !== null && value.mediaanidownloadnopeus !== undefined) {
            return this.createMediaaniTableString(value.mediaanidownloadnopeus)
        }
        return ""
    }

    createMediaaniUploadTableString(value: any): string {
        if (value.mediaaniuploadnopeus !== null && value.mediaaniuploadnopeus !== undefined) {
            return this.createMediaaniTableString(value.mediaaniuploadnopeus)
        }
        return ""
    }

    createMediaaniTableString(nopeus: any): string {
        let tableCellString = ""
        if (nopeus < 1) {
            tableCellString = "<1 Mbit/s"
        }
        else {
            tableCellString = ("{0} Mbit/s").replace("{0}", nopeus.toString())
        }
        return tableCellString
    }

    get bittimittariVuorokausiTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Bittimittari.RightSlot.VuorokaudenAika") ?? "App.Bittimittari.RightSlot.VuorokaudenAika")
        columns.push(t("App.Bittimittari.RightSlot.MediaaniDownloadNopeus") ?? "App.Bittimittari.RightSlot.MediaaniDownloadNopeus")
        columns.push(t("App.Bittimittari.RightSlot.MittaustenLkm") ?? "App.Bittimittari.RightSlot.MittaustenLkm")
        return columns
    }

    get bittimittariOperaattoriTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Bittimittari.RightSlot.Operaattori") ?? "App.Bittimittari.RightSlot.Operaattori")
        columns.push(t("App.Bittimittari.RightSlot.MediaaniDownloadNopeus") ?? "App.Bittimittari.RightSlot.MediaaniDownloadNopeus")
        columns.push(t("App.Bittimittari.RightSlot.MediaaniUploadNopeus") ?? "App.Bittimittari.RightSlot.MediaaniUploadNopeus")
        columns.push(t("App.Bittimittari.RightSlot.MittaustenLkm") ?? "App.Bittimittari.RightSlot.MittaustenLkm")
        return columns
    }

    // #endregion
}
